import type {
  DocumentoPadrao,
  DocumentoPadraoFormInput,
  DocumentoPadraoParte,
  DocumentoPadraoUso,
  DocumentoPadraoUsoOrigem,
  DocumentosPadraoConsulta,
  DocumentosPadraoContexto,
  DocumentosPadraoDados,
} from '@/features/documentosPadrao/types/documentosPadraoTypes';
import { getApiErrorMessage } from '@/shared/utils/apiResponse';
import { ErroHttp, lerRetryAfterSegundos } from '@/shared/utils/erroHttp';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  downloadBlob,
  request,
  visualizarBlob,
} from '@/shared/utils/requestBuilder';

const BASE = '/documentos-padrao';

/** Tamanho da página de "onde está sendo usado". O drawer usa o mesmo número para pedir a próxima. */
export const TAMANHO_PAGINA_USOS = 20;

/**
 * Boundary DTO → domínio. Ele existe por duas razões:
 *
 * - o servidor manda autor como `{ id, nome }` e a tela mostra o nome; converter aqui evita mexer
 *   em dez componentes só por causa da forma do payload;
 * - `statusRotulo` e `href` de um uso NÃO vêm do servidor — rótulo é apresentação e rota é do
 *   front, que muda sem migration. Eles são derivados aqui, de `origem` + `origemId`.
 */

interface AutorDto {
  id: string;
  nome: string;
}

interface UsoDto {
  id: string;
  origem: DocumentoPadraoUsoOrigem;
  origemId: string;
  versao: number;
  referencia: string;
  titulo: string;
  usadoEm: string;
  emAndamento: boolean;
}

interface DocumentoPadraoDto {
  id: string;
  nome: string;
  tipo: DocumentoPadrao['tipo'];
  escopo: DocumentoPadrao['escopo'];
  produtos: DocumentoPadrao['produtos'];
  setoresIds: string[];
  validoAte: string | null;
  parteVinculada: DocumentoPadrao['parteVinculada'];
  arquivo: DocumentoPadrao['arquivo'];
  versaoAtual: number;
  criadoPor: AutorDto;
  criadoEm: string;
  atualizadoEm: string;
  arquivado: boolean;
  arquivadoEm: string | null;
  arquivadoPor: AutorDto | null;
  revisao: number;
  versoes?: Array<{
    id: string;
    versao: number;
    arquivoId: string;
    nomeArquivo: string;
    tamanhoBytes: number;
    enviadoPor: AutorDto;
    enviadoEm: string;
    origemVersao: number | null;
  }>;
  usos?: UsoDto[];
  totalUsos?: number;
  usosEmAndamento: number;
}

const ROTA_POR_ORIGEM: Record<DocumentoPadraoUsoOrigem, string> = {
  PROCESSO_ARBITRAL: '/arbitral',
  NOTIFICACAO: '/notificacoes',
  NEGOCIACAO: '/negociacoes',
  MEDIACAO: '/mediacoes',
};

/**
 * Contrato do mock: uso EM ANDAMENTO não tem rótulo — é isso que faz o drawer mostrar "Abrir";
 * encerrado mostra o rótulo no lugar do link (review do PR #1141, COR-09).
 */
const paraUso = (dto: UsoDto): DocumentoPadraoUso => ({
  ...dto,
  statusRotulo: dto.emAndamento ? null : 'Encerrado',
  href: `${ROTA_POR_ORIGEM[dto.origem]}/${dto.origemId}`,
});

const paraDocumento = (dto: DocumentoPadraoDto): DocumentoPadrao => ({
  id: dto.id,
  nome: dto.nome,
  tipo: dto.tipo,
  arquivo: dto.arquivo,
  versaoAtual: dto.versaoAtual,
  versoes: (dto.versoes ?? []).map((versao) => ({
    ...versao,
    enviadoPor: versao.enviadoPor.nome,
  })),
  parteVinculada: dto.parteVinculada,
  escopo: dto.escopo,
  setoresIds: dto.setoresIds,
  produtos: dto.produtos,
  validoAte: dto.validoAte,
  criadoPor: dto.criadoPor.nome,
  criadoPorId: dto.criadoPor.id,
  criadoEm: dto.criadoEm,
  atualizadoEm: dto.atualizadoEm,
  arquivado: dto.arquivado,
  arquivadoEm: dto.arquivadoEm,
  arquivadoPor: dto.arquivadoPor?.nome ?? null,
  usos: (dto.usos ?? []).map(paraUso),
  totalUsos: dto.totalUsos ?? 0,
  usosEmAndamento: dto.usosEmAndamento,
  revisao: dto.revisao,
});

/**
 * Lê o envelope `{ data }` e, no erro, lança o `ErroHttp` do repo — não uma classe própria.
 *
 * Uma quarta classe de erro paralela ficaria invisível para `ehErroHttp`/`temCodigoHttp`, que é
 * como o resto do front decide sobre 429, sessão e retry: o tratamento genérico passaria reto por
 * ela. O `codigo` (`error.code` do core-api) é o que distingue o 409 da trava de exclusão do 409
 * de `revisao` desatualizada.
 */
async function lerCorpo<T>(response: Response, fallback: string): Promise<T> {
  let corpo: unknown = null;
  try {
    corpo = await response.json();
  } catch {
    corpo = null;
  }

  if (!response.ok) {
    const erro = (corpo as { error?: { code?: string } } | null)?.error;
    throw new ErroHttp(
      getApiErrorMessage(corpo, `${fallback} (status ${response.status})`),
      response.status,
      lerRetryAfterSegundos(response.headers),
      erro?.code ?? null,
    );
  }

  // Todo endpoint do core responde `ResponseHelper.success(x)` = `{ success, data: x }`. Na lista e
  // nos usos, `x` já é `{ data, pagination, ... }` — por isso `carregar`/`listarUsos` leem `.data`
  // de novo sobre o valor desembrulhado (review do PR #1141, COR-08).
  return (corpo as { data?: T } | null)?.data as T;
}

/** Cada mutação devolve o documento atualizado; só a mensagem de erro muda. */
async function lerDocumento(response: Response, fallback: string): Promise<DocumentoPadrao> {
  return paraDocumento(await lerCorpo<DocumentoPadraoDto>(response, fallback));
}

/** Filtro em "todos"/vazio não vai na query — o servidor já trata a ausência como sem filtro. */
function paraQuery(consulta: DocumentosPadraoConsulta): Record<string, string | number> {
  const { aba, filtros, page, limit } = consulta;
  return {
    aba,
    page,
    limit,
    ...(filtros.busca.trim() ? { busca: filtros.busca.trim() } : {}),
    ...(filtros.tipo !== 'todos' ? { tipo: filtros.tipo } : {}),
    ...(filtros.setorId && filtros.setorId !== 'todos' ? { setorId: filtros.setorId } : {}),
    ...(filtros.produto !== 'todos' ? { produto: filtros.produto } : {}),
    ...(filtros.validade !== 'todas' ? { validade: filtros.validade } : {}),
  };
}

/**
 * `FormData` como body, SEM tocar em `Content-Type`: o `requestBuilder` remove o header para o
 * browser pôr o boundary. `X-Workspace-Id` é injetado por ele — nenhum service passa nada.
 */
function paraFormData(input: DocumentoPadraoFormInput, arquivo: File): FormData {
  const form = new FormData();
  form.append('nome', input.nome);
  form.append('tipo', input.tipo);
  form.append('escopo', input.escopo);
  form.append('produtos', JSON.stringify(input.produtos));
  form.append('setoresIds', JSON.stringify(input.escopo === 'SETORES' ? input.setoresIds : []));
  if (input.validoAte) form.append('validoAte', input.validoAte);
  if (input.parteCatalogoId) form.append('parteCatalogoId', input.parteCatalogoId);
  form.append('arquivo', arquivo);
  return form;
}

interface RespostaListaDto {
  data: DocumentoPadraoDto[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  contadores: DocumentosPadraoDados['contadores'];
  resumoValidade: DocumentosPadraoDados['resumoValidade'];
  referencia: string;
  diasAvisoVencimento: number;
}

async function lerLista(response: Response): Promise<DocumentosPadraoDados> {
  const dados = await lerCorpo<RespostaListaDto>(
    response,
    'Não foi possível carregar os documentos padrão.',
  );

  return {
    itens: dados.data.map(paraDocumento),
    paginacao: dados.pagination,
    contadores: dados.contadores,
    resumoValidade: dados.resumoValidade,
    referencia: dados.referencia,
    diasAvisoVencimento: dados.diasAvisoVencimento,
  };
}

export const documentosPadraoService = {
  carregar: async (consulta: DocumentosPadraoConsulta): Promise<DocumentosPadraoDados> => {
    return lerLista(await apiGet(BASE, paraQuery(consulta)));
  },

  carregarParaArbitragem: async (
    consulta: DocumentosPadraoConsulta,
  ): Promise<DocumentosPadraoDados> => {
    // O painel precisa receber o 403 original: repetir sem workspace pode transformá-lo em 400.
    return lerLista(
      await request({
        endpoint: BASE,
        method: 'GET',
        params: paraQuery(consulta),
        recuperarWorkspaceNo403: false,
      }),
    );
  },

  carregarContexto: async (): Promise<DocumentosPadraoContexto> => {
    const response = await apiGet(`${BASE}/contexto`);
    return await lerCorpo<DocumentosPadraoContexto>(
      response,
      'Não foi possível carregar os setores do escritório.',
    );
  },

  /** Detalhe com versões e a primeira página de usos — o drawer não os tem pela lista. */
  detalhar: async (id: string): Promise<DocumentoPadrao> => {
    const response = await apiGet(`${BASE}/${id}`);
    return lerDocumento(response, 'Não foi possível carregar o documento.');
  },

  /** Páginas seguintes de "onde está sendo usado" — o detalhe traz só a primeira. */
  listarUsos: async (id: string, page: number): Promise<DocumentoPadraoUso[]> => {
    const response = await apiGet(`${BASE}/${id}/usos`, { page, limit: TAMANHO_PAGINA_USOS });
    const dados = await lerCorpo<{ data: UsoDto[] }>(
      response,
      'Não foi possível carregar os usos do documento.',
    );
    return dados.data.map(paraUso);
  },

  criar: async (input: DocumentoPadraoFormInput): Promise<DocumentoPadrao> => {
    // Arquivo é obrigatório de verdade: o fallback `documento.pdf` do mock morreu.
    if (!input.arquivo) {
      throw new ErroHttp('Selecione um arquivo para o documento.', 400);
    }
    const response = await apiPost(BASE, paraFormData(input, input.arquivo));
    return lerDocumento(response, 'Não foi possível salvar o documento.');
  },

  atualizar: async (
    id: string,
    input: DocumentoPadraoFormInput,
    revisao: number,
  ): Promise<DocumentoPadrao> => {
    const response = await apiPut(`${BASE}/${id}`, {
      nome: input.nome,
      tipo: input.tipo,
      escopo: input.escopo,
      produtos: input.produtos,
      setoresIds: input.escopo === 'SETORES' ? input.setoresIds : [],
      validoAte: input.validoAte,
      parteCatalogoId: input.parteCatalogoId,
      revisao,
    });
    return lerDocumento(response, 'Não foi possível salvar as alterações.');
  },

  substituirArquivo: async (id: string, arquivo: File): Promise<DocumentoPadrao> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    const response = await apiPost(`${BASE}/${id}/versoes`, form);
    return lerDocumento(response, 'Não foi possível substituir o arquivo.');
  },

  restaurarVersao: async (id: string, versao: number): Promise<DocumentoPadrao> => {
    const response = await apiPost(`${BASE}/${id}/versoes/${versao}/restaurar`);
    return lerDocumento(response, 'Não foi possível restaurar a versão.');
  },

  duplicar: async (id: string): Promise<DocumentoPadrao> => {
    const response = await apiPost(`${BASE}/${id}/duplicar`);
    return lerDocumento(response, 'Não foi possível duplicar o documento.');
  },

  arquivar: async (id: string): Promise<DocumentoPadrao> => {
    const response = await apiPatch(`${BASE}/${id}/arquivamento`, { arquivado: true });
    return lerDocumento(response, 'Não foi possível arquivar o documento.');
  },

  restaurar: async (id: string): Promise<DocumentoPadrao> => {
    const response = await apiPatch(`${BASE}/${id}/arquivamento`, { arquivado: false });
    return lerDocumento(response, 'Não foi possível restaurar o documento.');
  },

  excluir: async (id: string): Promise<string> => {
    const response = await apiDelete(`${BASE}/${id}`);
    await lerCorpo<{ id: string }>(response, 'Não foi possível excluir o documento.');
    return id;
  },

  /**
   * Seletor de parte do formulário. Consome o endpoint que JÁ EXISTE e é dono do catálogo
   * (`/partes/recentes`, agora com `busca`) — não se cria endpoint de partes dentro desta feature.
   * O que sobe no formulário é `parteCatalogoId`, nunca nome/CPF digitados.
   */
  buscarPartes: async (busca: string): Promise<DocumentoPadraoParte[]> => {
    const response = await apiGet('/partes/recentes', {
      limit: 20,
      ...(busca.trim() ? { busca: busca.trim() } : {}),
    });
    const contatos = await lerCorpo<
      Array<{ parteCatalogoId: string; nome: string; cpfCnpj: string }>
    >(response, 'Não foi possível buscar as partes.');

    return contatos.map((contato) => ({
      id: contato.parteCatalogoId,
      nome: contato.nome,
      cpfCnpj: contato.cpfCnpj || null,
    }));
  },

  /**
   * Pré-visualizar e baixar passam pelo PROXY DE BYTES da API — `fetch`, nunca navegação: o header
   * `X-Workspace-Id` é injetado dentro do request, e um `<a href>` não o carrega.
   */
  preVisualizar: (id: string, versao?: number): Promise<void> =>
    visualizarBlob(`${BASE}/${id}/conteudo?disposition=inline${versao ? `&versao=${versao}` : ''}`),

  baixar: (id: string, nomeArquivo: string, versao?: number): Promise<void> =>
    downloadBlob(
      `${BASE}/${id}/conteudo?disposition=attachment${versao ? `&versao=${versao}` : ''}`,
      nomeArquivo,
    ),
};
